import { useState, useEffect, useCallback, useContext } from "react";
import "./ReportsHub.css";
import { FaCrown, FaTrash, FaDownload, FaChartBar } from "react-icons/fa";
import { FaArrowLeftLong } from "react-icons/fa6";
import { useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { ThemeContext } from "../context/themeContext";
import { apiFetch } from "../utils/apiFetch";


/* ── Mini Score Ring ─────────────────────────────────────────── */
function ScoreRing({ score, max = 600 }) {
  const radius = 28;
  const circ = 2 * Math.PI * radius;
  const clamped = Math.min(Math.max(score, 0), max);
  const dash = (clamped / max) * circ;
  const color =
    clamped >= 500 ? "#18a558" :
    clamped >= 400 ? "#3cba6e" :
    clamped >= 300 ? "#ffc66a" : "#ff6b6b";
  return (
    <svg viewBox="0 0 72 72" width="72" height="72" className="rh-ring-svg">
      <circle cx="36" cy="36" r={radius} stroke="#e6e6e6" strokeWidth="5" fill="none" />
      <circle
        cx="36" cy="36" r={radius}
        stroke={color} strokeWidth="5" strokeLinecap="round" fill="none"
        transform="rotate(-90 36 36)"
        strokeDasharray={`${dash} ${circ - dash}`}
      />
      <text x="50%" y="52%" dominantBaseline="middle" textAnchor="middle"
        fontSize="13" fontWeight="800" fill={color}>{score}</text>
    </svg>
  );
}

function getClassification(s) {
  if (s >= 500) return { label: "Validated Visionary", color: "#18a558" };
  if (s >= 400) return { label: "Promising Potential", color: "#3cba6e" };
  if (s >= 300) return { label: "Needs Work",          color: "#f59e0b" };
  return              { label: "High Risk",             color: "#ef4444" };
}

/* ═══════════════════════════════════════════════════════════════
   REPORTS HUB
═══════════════════════════════════════════════════════════════ */
export default function ReportsHub({ setToggle, onClose }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, loading: userLoading } = useContext(UserContext);
  const { activeTheme }                = useContext(ThemeContext);
  const dark = activeTheme === "dark";

  const initialTab =
    location.state?.tab === "mm" || location.state?.tab === "mind-mapping"
      ? "mm"
      : "idea";
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (location.state?.tab) {
      const t =
        location.state.tab === "mm" || location.state.tab === "mind-mapping"
          ? "mm"
          : "idea";
      setActiveTab(t);
    }
  }, [location.state?.tab]);

  const isBypassUser =
    user?.email?.trim().toLowerCase() === "marsmatedemo@gmail.com" ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  /* ── Idea Validation state ─────────────────────────────────── */
  const [ideaReports,      setIdeaReports]      = useState([]);
  const [ideaLoading,      setIdeaLoading]      = useState(true);
  const [ideaError,        setIdeaError]        = useState(false);
  const [ideaDeleteTarget, setIdeaDeleteTarget] = useState(null);
  const [ideaDeleting,     setIdeaDeleting]     = useState(false);

  /* ── Mind Mapping state ────────────────────────────────────── */
  const [mmReports,      setMmReports]      = useState([]);
  const [mmLoading,      setMmLoading]      = useState(true);
  const [mmError,        setMmError]        = useState(false);
  const [mmDeleteTarget, setMmDeleteTarget] = useState(null);
  const [mmDeleting,     setMmDeleting]     = useState(false);

  /* ── Fetch with 1 auto-retry on failure ────────────────────── */
  const fetchIdeaReports = useCallback(async (userId) => {
    setIdeaError(false);
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res  = await apiFetch(
          `/idea-validation/report-list/${userId}`
        );
        const data = await res.json();
        if (data?.success) {
          setIdeaReports(data.reports || []);
          setIdeaLoading(false);
          return;
        }
        if (attempt === 1) setIdeaError(true);
      } catch {
        if (attempt === 1) setIdeaError(true);
        else await new Promise(r => setTimeout(r, 1200));
      }
    }
    setIdeaLoading(false);
  }, []);

  const fetchMmReports = useCallback(async (userId) => {
    setMmError(false);
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res  = await apiFetch(
          `/api/mind-mapping/report-list/${userId}`
        );
        const data = await res.json();
        if (data?.success) {
          setMmReports(data.reports || []);
          setMmLoading(false);
          return;
        }
        if (attempt === 1) setMmError(true);
      } catch {
        if (attempt === 1) setMmError(true);
        else await new Promise(r => setTimeout(r, 1200));
      }
    }
    setMmLoading(false);
  }, []);

  useEffect(() => {
    ["proButtonActive","premiumButtonActive","mindproButtonActive",
     "mindpremiumButtonActive","mindReports","reportAccess","reportBAccess"]
      .forEach(k => localStorage.removeItem(k));

    if (!userLoading && user?.user_id) {
      fetchIdeaReports(user.user_id);
      fetchMmReports(user.user_id);
    } else if (!userLoading && !user) {
      setIdeaLoading(false);
      setMmLoading(false);
    }
  }, [user, userLoading, fetchIdeaReports, fetchMmReports]);

  /* ── Scorecard inline ₹10 payment ─────────────────────────── */
  const [payingReport, setPayingReport]   = useState(null); // report object being paid for
  const [rhPayLoading, setRhPayLoading]   = useState(false);
  const [rhPaySuccess, setRhPaySuccess]   = useState(false);

  const handleScorecardPay = async (report) => {
    const BACKEND = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
    setPayingReport(report);
    setRhPayLoading(true);
    try {
      // Ensure Razorpay SDK is loaded
      await new Promise((resolve, reject) => {
        if (window.Razorpay) { resolve(); return; }
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });

      const res = await fetch(`${BACKEND}/idea-validation/create-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const orderData = await res.json();
      if (!orderData?.success) { alert("Failed to create payment order."); setRhPayLoading(false); return; }

      const razorpayKey = orderData.key_id
        || process.env.REACT_APP_RAZORPAY_KEY_ID_IDEA_VALIDATION
        || process.env.REACT_APP_RAZORPAY_KEY_ID;
      if (!razorpayKey) { alert("Payment configuration error."); setRhPayLoading(false); return; }

      const options = {
        key: razorpayKey,
        amount: orderData.amount,
        currency: "INR",
        name: "Marsmate AI",
        description: "Idea Validation Scorecard Unlock",
        order_id: orderData.order_id,
        prefill: { name: user?.name || "User", email: user?.email || "" },
        theme: { color: "#F45900" },
        handler: async (response) => {
          try {
            const verifyRes = await fetch(`${BACKEND}/idea-validation/verify-payment`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                recordId: report.id,
              }),
              credentials: "include",
            });
            const verifyData = await verifyRes.json();
            if (verifyData.success) {
              localStorage.setItem("scorecardAccess", "true");
              localStorage.setItem("payingReportId", report.id);
              // Mark the report as paid locally so UI updates immediately
              setIdeaReports(prev => prev.map(r =>
                String(r.id) === String(report.id) ? { ...r, paymentStatus: "PAID" } : r
              ));
              setRhPaySuccess(true);
            } else {
              alert("Payment verification failed.");
            }
          } catch (err) {
            console.error(err);
            alert("Error verifying payment.");
          }
        },
        modal: { ondismiss: () => setRhPayLoading(false) },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (r) => { alert("Payment Failed: " + r.error.description); setRhPayLoading(false); });
      rzp.open();
    } catch (err) {
      console.error(err);
      alert("Error initiating payment.");
    } finally {
      setRhPayLoading(false);
    }
  };

  /* ── Delete ────────────────────────────────────────────────── */
  const deleteIdea = async () => {
    if (!ideaDeleteTarget) return;
    setIdeaDeleting(true);
    try {
      const res  = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/idea-validation/delete-report/${ideaDeleteTarget}`,
        { method: "DELETE", credentials: "include" }
      );
      const data = await res.json();
      if (data.success) setIdeaReports(p => p.filter(r => String(r.id) !== String(ideaDeleteTarget)));
    } catch {}
    finally { setIdeaDeleting(false); setIdeaDeleteTarget(null); }
  };

  const deleteMm = async () => {
    if (!mmDeleteTarget) return;
    setMmDeleting(true);
    try {
      const res  = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/mind-mapping/delete-report/${mmDeleteTarget}`,
        { method: "DELETE", credentials: "include" }
      );
      const data = await res.json();
      if (data.success) setMmReports(p => p.filter(r => String(r.id) !== String(mmDeleteTarget)));
    } catch {}
    finally { setMmDeleting(false); setMmDeleteTarget(null); }
  };

  /* ── Subscription nav ──────────────────────────────────────── */
  const goToSub = (report, type, isMind = false) => {
    setToggle(false); onClose();
    ["proButtonActive","premiumButtonActive","mindproButtonActive","mindpremiumButtonActive"]
      .forEach(k => localStorage.removeItem(k));

    if (isMind) {
      localStorage.setItem("mindReports", "true");
      localStorage.setItem("mindpayingReportId", report.id);
      localStorage.setItem(type === "A" ? "mindproButtonActive" : "mindpremiumButtonActive", "true");
      localStorage.setItem("mindpayingReportType", type);
    } else {
      localStorage.setItem("payingReportId", report.id);
      localStorage.setItem(type === "A" ? "proButtonActive" : "premiumButtonActive", "true");
      localStorage.setItem("payingReportType", type);
    }
    navigate(`/report-subscription`, { state: { report } });
  };

  /* ── Open MM Dashboard directly (bypasses the list page) ───── */
  const [mmDashLoading, setMmDashLoading] = useState(null); // holds report.id while loading

  const openMmDashboard = async (report) => {
    setMmDashLoading(report.id);
    try {
      let courseId = 1;
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mind-mapping/course-id/${encodeURIComponent(report.course)}`, { credentials: "include" });
        if (r.ok) { const d = await r.json(); courseId = d.id || 1; }
      } catch {}

      let countryId = 1;
      let stateId = 1;
      try {
        const rC = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/countries`, { credentials: "include" });
        if (rC.ok) {
          const countries = await rC.json();
          const match = countries.find(c => c.name?.toLowerCase() === report.country?.toLowerCase());
          if (match) countryId = match.id;
        }
        const rS = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/states/${countryId}`, { credentials: "include" });
        if (rS.ok) {
          const states = await rS.json();
          const match = states.find(s => s.name?.toLowerCase() === report.state?.toLowerCase());
          if (match) stateId = match.id;
        }
      } catch {}

      let goalIds = [];
      try {
        const rG = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mind-mapping/goals`, { credentials: "include" });
        if (rG.ok) {
          const goalsList = await rG.json();
          const goalMap = {};
          goalsList.forEach(g => { goalMap[g.name.toLowerCase().trim()] = g.id; });
          goalIds = (report.goals || []).map(n => goalMap[n.toLowerCase().trim()]).filter(Boolean);
        }
      } catch {}

      const goalNameToId = { "learning & knowledge": 1, "technology & digital behavior": 2, "work & career habits": 3, "money & finance": 4, "health & wellness": 5, "lifestyle & daily routines": 6, "social & community behavior": 7, "entertainment & creativity": 8, "shopping & consumption": 9, "problem-solving & frustrations": 10, "values & beliefs": 11, "curiosity & exploration": 12 };
      if (!goalIds.length) goalIds = (report.goals || []).map(n => goalNameToId[n.toLowerCase().trim()]).filter(Boolean);
      if (goalIds.length !== 6) goalIds = [1, 2, 3, 4, 5, 6];

      localStorage.setItem("mind-mapping-access",    "true");
      localStorage.setItem("selectedSector",          report.sector);
      localStorage.setItem("selectedSectorType",      report.sector_type);
      localStorage.setItem("selectedState",           report.state);
      localStorage.setItem("selectedDistrict",        report.district);
      localStorage.setItem("course_id",               String(courseId));
      localStorage.setItem("selectedStateId",         String(stateId));
      localStorage.setItem("graduation_course",       report.course);
      localStorage.setItem("department",              Array.isArray(report.departments) ? report.departments[0] : report.departments);
      localStorage.setItem("selected_goals",          JSON.stringify(goalIds));

      close();
      navigate(`/mind-mapping/sec-b`);
    } catch (err) {
      console.error("Failed to open dashboard:", err);
    } finally {
      setMmDashLoading(null);
    }
  };

  /* ── Nav helpers ───────────────────────────────────────────── */
  const close = () => { setToggle(false); onClose(); };

  const loading = activeTab === "idea" ? (userLoading || ideaLoading) : (userLoading || mmLoading);

  /* ════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════ */
  return (
    <div className={`rh-wrapper${dark ? " dark-mode" : ""}`}>
      <div className="rh-fog" aria-hidden="true" />

      {/* ── Page header ─────────────────────────────────────── */}
      <div className="rh-header">
        <button className="rh-back-btn" onClick={() => { close(); navigate("/dashboard"); }}>
          <FaArrowLeftLong size={18} />
        </button>
        <div>
          <h1 className="rh-title">Reports</h1>
          <p className="rh-subtitle">View, download and analyse your reports</p>
        </div>
      </div>

      {/* ── Tab pill switcher ────────────────────────────────── */}
      <div className="rh-tabs">
        <button
          className={`rh-tab${activeTab === "idea" ? " active" : ""}`}
          onClick={() => setActiveTab("idea")}
        >
          📋 Idea Validation Reports
          {!ideaLoading && (
            <span className="rh-tab-count">{ideaReports.length}</span>
          )}
        </button>
        <button
          className={`rh-tab${activeTab === "mm" ? " active mm" : ""}`}
          onClick={() => setActiveTab("mm")}
        >
          🗺️ Mind Mapping Reports
          {!mmLoading && (
            <span className="rh-tab-count mm-count">{mmReports.length}</span>
          )}
        </button>
      </div>

      {/* ── Content ─────────────────────────────────────────── */}
      <div className="rh-content">
        {loading ? (
          <div className="rh-grid">
            {[1, 2, 3].map((i) => (
              <div className="rh-card rh-card-skeleton" key={i}>
                <div className="rh-skel-line rh-skel-title" />
                <div className="rh-skel-line rh-skel-sub" />
                <div className="rh-skel-rect" />
                <div className="rh-skel-line rh-skel-btn" />
              </div>
            ))}
          </div>

        ) : activeTab === "idea" ? (
          /* ════════ IDEA VALIDATION TAB ════════ */
          ideaError ? (
            <div className="rh-empty">
              <div className="rh-empty-icon">⚠️</div>
              <p className="rh-empty-msg">Failed to load your reports.</p>
              <p className="rh-empty-sub">Check your connection and try again.</p>
              <button className="rh-empty-cta" onClick={() => { setIdeaLoading(true); fetchIdeaReports(user?.user_id); }}>
                Retry
              </button>
            </div>
          ) : ideaReports.length === 0 ? (
            <div className="rh-empty">
              <div className="rh-empty-icon">📋</div>
              <p className="rh-empty-msg">No Idea Validation reports yet.</p>
              <p className="rh-empty-sub">Complete the Idea Validation form to generate your first report.</p>
              <button className="rh-empty-cta" onClick={() => { close(); navigate("/dashboard"); }}>
                Start Idea Validation
              </button>
            </div>
          ) : (
            <div className="rh-grid">
              {ideaReports.map(report => {
                const paid       = report?.paidReports || [];
              const isPro      = paid.includes("A");
              const isPremium  = paid.includes("B");
              const cls        = getClassification(report.totalScore || 0);

              return (
                <div className="rh-card" key={report.id}>

                  {/* Card top bar */}
                  <div className="rh-card-top">
                    <div className="rh-card-company-wrap">
                      <span className="rh-card-dot idea-dot" />
                      <span className="rh-card-company">{report.companyName || "Unnamed Company"}</span>
                    </div>
                    <div className="rh-card-meta-right">
                      <span className="rh-card-date">
                        {new Date(report.createdAt).toLocaleDateString("en-IN",
                          { day: "2-digit", month: "short", year: "numeric" })}
                      </span>
                      <button className="rh-delete-btn" title="Delete report"
                        onClick={() => setIdeaDeleteTarget(report.id)}>
                        <FaTrash size={11} />
                      </button>
                    </div>
                  </div>

                  {/* Two frames */}
                  <div className="rh-frames">

                    {/* ── Frame 1: Reports ── */}
                    <div className="rh-frame rh-frame-left">
                      <div className="rh-frame-heading">
                        <span className="rh-fh-pill idea-pill">📄 Reports</span>
                      </div>

                      {/* Report A — Pro */}
                      <div className="rh-tier-row">
                        <div className="rh-tier-labels">
                          <span className="rh-tier-name">Report A</span>
                          <span className="rh-badge silver"><FaCrown size={9} /> Pro</span>
                          <span className="rh-score-chip">
                            {report.totalScore || 0} / 600
                          </span>
                        </div>
                        {isPro || isBypassUser ? (
                          <button className="rh-btn rh-btn-download" onClick={() => {
                            close();
                            localStorage.setItem("reportAccess", true);
                            navigate(`/report`,
                              { state: { reportData: report } });
                          }}>
                            <FaDownload size={11} /> View & Download
                          </button>
                        ) : (
                          <button className="rh-btn rh-btn-pay" onClick={() => goToSub(report, "A")}>
                            ₹199 · Unlock Pro
                          </button>
                        )}
                      </div>

                      <div className="rh-tier-sep" />

                      {/* Report A+ — Premium */}
                      <div className="rh-tier-row">
                        <div className="rh-tier-labels">
                          <span className="rh-tier-name">Report A+</span>
                          <span className="rh-badge gold"><FaCrown size={9} /> Premium</span>
                        </div>
                        {isPremium || isBypassUser ? (
                          <button className="rh-btn rh-btn-download" onClick={() => {
                            close();
                            localStorage.setItem("reportBAccess", true);
                            navigate(`/report-b`,
                              { state: { reportData: report } });
                          }}>
                            <FaDownload size={11} /> View & Download
                          </button>
                        ) : (
                          <button className="rh-btn rh-btn-pay" onClick={() => goToSub(report, "B")}>
                            ₹299 · Unlock Premium
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="rh-frame-divider" />

                    {/* ── Frame 2: Scorecard ── */}
                    {(() => {
                      const isLocked = report.paymentStatus === "PENDING" && !isBypassUser;
                      return (
                        <div className={`rh-frame rh-frame-right rh-frame-scorecard${isLocked ? " rh-frame-locked" : ""}`}>
                          <div className="rh-frame-heading">
                            <span className="rh-fh-pill score-pill">📊 Scorecard</span>
                          </div>

                          {/* Blurred scorecard content for locked reports */}
                          <div className={isLocked ? "rh-scorecard-blurred" : ""}>
                            <div className="rh-scorecard-body">
                              <ScoreRing score={report.totalScore || 0} />
                              <div className="rh-score-detail">
                                <span className="rh-score-big" style={{ color: cls.color }}>
                                  {report.totalScore || 0}<span className="rh-score-max">/600</span>
                                </span>
                                <span className="rh-classification" style={{ color: cls.color }}>
                                  {cls.label}
                                </span>
                              </div>
                            </div>
                            <div className="rh-scorecard-actions">
                              <button className="rh-btn rh-btn-scorecard" onClick={() => {
                                localStorage.setItem("scorecardData", JSON.stringify(report));
                                localStorage.setItem("scorecardAccess", "true");
                                close();
                                navigate(`/scorecard`, {
                                  state: { fromReportsHub: true }
                                });
                              }}>
                                Show Scorecard
                              </button>
                              <button className="rh-btn rh-btn-analysis" onClick={() => {
                                localStorage.setItem("scorecardData", JSON.stringify(report));
                                localStorage.setItem("scorecardAccess", "true");
                                close();
                                navigate(`/scorecard`, {
                                  state: { openAnalysis: true, fromReportsHub: true }
                                });
                              }}>
                                🔍 Full Analysis
                              </button>
                            </div>
                          </div>

                          {/* Overlay for locked / unpaid scorecards */}
                          {isLocked && (
                            <div className="rh-lock-overlay">
                              <div className="rh-lock-icon-wrap">🔒</div>
                              <p className="rh-lock-label">Unlock for ₹10</p>
                              <button
                                className="rh-btn rh-btn-view-report"
                                disabled={rhPayLoading && payingReport?.id === report.id}
                                onClick={() => handleScorecardPay(report)}
                              >
                                {rhPayLoading && payingReport?.id === report.id ? "Processing..." : "View Report"}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                  </div>
                </div>
              );
            })}
          </div>
        )

        ) : (
          /* ════════ MIND MAPPING TAB ════════ */
          mmError ? (
            <div className="rh-empty">
              <div className="rh-empty-icon">⚠️</div>
              <p className="rh-empty-msg">Failed to load your reports.</p>
              <p className="rh-empty-sub">Check your connection and try again.</p>
              <button className="rh-empty-cta mm" onClick={() => { setMmLoading(true); fetchMmReports(user?.user_id); }}>
                Retry
              </button>
            </div>
          ) : mmReports.length === 0 ? (
            <div className="rh-empty">
              <div className="rh-empty-icon">🗺️</div>
              <p className="rh-empty-msg">No Mind Mapping reports yet.</p>
              <p className="rh-empty-sub">Complete the Mind Mapping form to generate your first report.</p>
              <button className="rh-empty-cta mm" onClick={() => { close(); navigate("/dashboard"); }}>
                Start Mind Mapping
              </button>
            </div>
          ) : (
            <div className="rh-grid">
              {mmReports.map(report => {
                const paid      = report?.paid_reports || [];
              const isPro     = paid.includes("A");
              const isPremium = paid.includes("B");
              const title     = report.idea_title || report.sector || "Mind Mapping Report";

              return (
                <div className="rh-card rh-card-mm" key={report.id}>

                  {/* Card top bar */}
                  <div className="rh-card-top">
                    <div className="rh-card-company-wrap">
                      <span className="rh-card-dot mm-dot" />
                      <span className="rh-card-company">{title}</span>
                    </div>
                    <div className="rh-card-meta-right">
                      <span className="rh-card-date">
                        {new Date(report.created_at).toLocaleDateString("en-IN",
                          { day: "2-digit", month: "short", year: "numeric" })}
                      </span>
                      <button className="rh-delete-btn" title="Delete report"
                        onClick={() => setMmDeleteTarget(report.id)}>
                        <FaTrash size={11} />
                      </button>
                    </div>
                  </div>

                  {/* Two frames */}
                  <div className="rh-frames">

                    {/* ── Frame 1: Reports ── */}
                    <div className="rh-frame rh-frame-left">
                      <div className="rh-frame-heading">
                        <span className="rh-fh-pill mm-pill">📄 Reports</span>
                      </div>

                      {/* Report A */}
                      <div className="rh-tier-row">
                        <div className="rh-tier-labels">
                          <span className="rh-tier-name">Report A</span>
                          <span className="rh-badge silver"><FaCrown size={9} /> Pro</span>
                        </div>
                        {isPro || isBypassUser ? (
                          <button className="rh-btn rh-btn-download mm" onClick={() => {
                            close();
                            navigate(`/mind-mapping-report-a`,
                              { state: { reportData: report } });
                          }}>
                            <FaDownload size={11} /> View & Download
                          </button>
                        ) : (
                          <button className="rh-btn rh-btn-pay mm" onClick={() => goToSub(report, "A", true)}>
                            ₹199 · Unlock Pro
                          </button>
                        )}
                      </div>

                      <div className="rh-tier-sep" />

                      {/* Report A+ */}
                      <div className="rh-tier-row">
                        <div className="rh-tier-labels">
                          <span className="rh-tier-name">Report A+</span>
                          <span className="rh-badge gold"><FaCrown size={9} /> Premium</span>
                        </div>
                        {isPremium || isBypassUser ? (
                          <button className="rh-btn rh-btn-download mm" onClick={() => {
                            close();
                            navigate(`/mind-mapping-report-b`,
                              { state: { reportData: report } });
                          }}>
                            <FaDownload size={11} /> View & Download
                          </button>
                        ) : (
                          <button className="rh-btn rh-btn-pay mm" onClick={() => goToSub(report, "B", true)}>
                            ₹299 · Unlock Premium
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="rh-frame-divider" />

                    {/* ── Frame 2: Dashboard ── */}
                    <div className="rh-frame rh-frame-right rh-frame-dashboard">
                      <div className="rh-frame-heading">
                        <span className="rh-fh-pill dash-pill">📈 Dashboard</span>
                      </div>
                      <div className="rh-dash-tiles">
                        {["Goals","Sectors","Skills","District","Technologies","Courses"].map(t => (
                          <span key={t} className="rh-dash-tile">{t}</span>
                        ))}
                      </div>
                      <button
                        className="rh-btn rh-btn-dashboard"
                        disabled={mmDashLoading === report.id}
                        onClick={() => openMmDashboard(report)}
                      >
                        <FaChartBar size={11} />
                        {mmDashLoading === report.id ? "Loading…" : "Open Dashboard"}
                      </button>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        )
        )}
      </div>

      {/* ── Payment success modal ──────────────────────────────── */}
      {rhPaySuccess && (
        <div className="rh-modal-overlay" onClick={() => setRhPaySuccess(false)}>
          <div className="rh-modal rh-pay-success-modal" onClick={e => e.stopPropagation()} style={{ position: "relative" }}>
            <button
              onClick={() => setRhPaySuccess(false)}
              style={{ position: "absolute", top: "12px", right: "14px", background: "none", border: "none", color: "#aaa", fontSize: "1.2rem", cursor: "pointer" }}
              title="Close"
            >✕</button>
            <div className="rh-pay-success-tick">✓</div>
            <h3>Payment Successful!</h3>
            <p>Your scorecard is now unlocked. You can view it below.</p>
            <div className="rh-modal-actions">
              <button className="rh-btn rh-btn-view-report" onClick={() => {
                setRhPaySuccess(false);
                if (payingReport) {
                  localStorage.setItem("scorecardData", JSON.stringify({ ...payingReport, paymentStatus: "PAID" }));
                  localStorage.setItem("scorecardAccess", "true");
                  close();
                  navigate(`/scorecard`, { state: { fromReportsHub: true } });
                }
              }}>
                View Scorecard
              </button>
              <button className="rh-btn rh-btn-ghost" onClick={() => setRhPaySuccess(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirmation modal ────────────────────────── */}
      {(ideaDeleteTarget || mmDeleteTarget) && (
        <div className="rh-modal-overlay" onClick={() => { setIdeaDeleteTarget(null); setMmDeleteTarget(null); }}>
          <div className="rh-modal" onClick={e => e.stopPropagation()}>
            <div className="rh-modal-icon">🗑️</div>
            <h3>Delete Report?</h3>
            <p>This action cannot be undone.</p>
            <div className="rh-modal-actions">
              <button className="rh-btn rh-btn-danger"
                onClick={ideaDeleteTarget ? deleteIdea : deleteMm}
                disabled={ideaDeleting || mmDeleting}>
                {ideaDeleting || mmDeleting ? "Deleting…" : "Yes, Delete"}
              </button>
              <button className="rh-btn rh-btn-ghost"
                onClick={() => { setIdeaDeleteTarget(null); setMmDeleteTarget(null); }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
